import React, { useState, useEffect } from "react"; // Added useState and useEffect
import "./MusicPage.scss";

// Base data for tracks, without likes or isLiked initially
const baseTracksData = [
  {
    id: 1,
    title: "crush on you",
    artist: "Josh Aragon",
    albumArtUrl: "https://via.placeholder.com/120/1351d8/FFFFFF?Text=JA1", // May not be used by iframe
    googleDriveFileId: "1wJhivJYSqyyuFG9AO9H8Lq6R1JZhxnU4",
  },
  {
    id: 2,
    title: "i miss how it felt",
    artist: "Josh Aragon",
    albumArtUrl: "https://via.placeholder.com/120/1351d8/FFFFFF?Text=JA2",
    googleDriveFileId: "1HUHnyAq_2FbCpn996V0nTjOYmSx0Li-B",
  },
  {
    id: 3,
    title: "in your arms",
    artist: "Josh Aragon",
    albumArtUrl: "https://via.placeholder.com/120/1351d8/FFFFFF?Text=JA3",
    googleDriveFileId: "1SWb944l1UHUggWfPZLEOgw9Vb13BboXk",
  },
  {
    id: 4,
    title: "little things",
    artist: "Josh Aragon",
    albumArtUrl: "https://via.placeholder.com/120/1351d8/FFFFFF?Text=JA4",
    googleDriveFileId: "1DzOC8jP1J8qY-o-BqSJleFunlQfCb48O",
  },
];

// WaveformPlaceholder is no longer needed for iframe embeds
// const WaveformPlaceholder = () => ( ... );

const MusicCard = ({ track, onLikeTrack, isLiking }) => {
  // Construct the Google Drive embed URL for preview
  const embedUrl = `https://drive.google.com/file/d/${track.googleDriveFileId}/preview`;

  return (
    <div className="music-card embed-card">
      {" "}
      {/* Added embed-card class for specific styling */}
      {/* Album art and custom track info can still be displayed above or next to the iframe if desired */}
      <div className="track-meta-info">
        {/* <img src={track.albumArtUrl} alt={`${track.title} album art`} className="album-art-embed" /> */}
        <div className="title-artist-embed">
          <h3 className="track-title">{track.title}</h3>
          <p className="artist-name">by {track.artist}</p>
        </div>
        <div className="like-section">
          <button
            onClick={() => onLikeTrack(track.id)}
            className={`like-button ${track.isLiked ? "liked" : ""}`}
            disabled={track.isLiked || isLiking === track.id} // Disable if liked or if this track is currently being liked
          >
            {track.isLiked ? "♥" : "♡"}{" "}
            {/* Filled heart if liked, empty if not */}
          </button>
          <span className="like-count">{track.likes}</span>
        </div>
      </div>
      <div className="iframe-container">
        <iframe
          src={embedUrl}
          width="100%"
          height="80" // Adjust height as needed for the compact Drive player
          frameBorder="0"
          allow="autoplay" // Autoplay might be blocked by browsers
          allowFullScreen
          title={track.title}
        ></iframe>
      </div>
    </div>
  );
};

const MusicPage = () => {
  const [tracks, setTracks] = useState(
    baseTracksData.map((track) => ({ ...track, likes: 0, isLiked: false }))
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLiking, setIsLiking] = useState(null); // Stores ID of track currently being liked via API

  useEffect(() => {
    const fetchLikesForAllTracks = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const updatedTracks = await Promise.all(
          baseTracksData.map(async (track) => {
            let responseText = "";
            try {
              const response = await fetch(`/api/likes/${track.id}`);
              responseText = await response.text(); // Read body once as text

              if (!response.ok) {
                let errorDetail = response.statusText;
                if (responseText && !responseText.trim().startsWith("<")) {
                  errorDetail = responseText;
                } else if (responseText) {
                  console.error(
                    `HTML error received for track ${track.id} (status ${response.status}):`,
                    responseText.substring(0, 500)
                  );
                }
                console.error(
                  `Failed to fetch likes for track ${track.id}: ${response.status} - ${errorDetail}`
                );
                return { ...track, likes: 0, isLiked: false };
              }

              // If response.ok, now try to parse the text as JSON
              const data = JSON.parse(responseText);
              const likedInSession =
                localStorage.getItem(`liked-${track.id}`) === "true";
              return {
                ...track,
                likes: data.likes || 0,
                isLiked: likedInSession,
              };
            } catch (err) {
              // Catches fetch errors, text reading errors, or JSON parsing errors
              console.error(`Error processing track ${track.id}:`, err.message);
              if (responseText) {
                console.error(
                  `Raw response for track ${track.id} (that caused error):`,
                  responseText.substring(0, 500)
                );
              }
              return { ...track, likes: 0, isLiked: false }; // Default on any error for this track
            }
          })
        );
        setTracks(updatedTracks);
      } catch (err) {
        // This is for Promise.all failing, less likely now errors are caught per-track
        console.error(
          "General error in fetchLikesForAllTracks Promise.all:",
          err
        );
        setError(err.message);
        setTracks(
          baseTracksData.map((track) => ({
            ...track,
            likes: 0,
            isLiked: false,
          }))
        );
      }
      setIsLoading(false);
    };

    fetchLikesForAllTracks();
  }, []);

  const handleLikeTrack = async (trackId) => {
    const trackToLike = tracks.find((t) => t.id === trackId);
    if (!trackToLike || trackToLike.isLiked || isLiking === trackId) {
      return;
    }

    setIsLiking(trackId);
    setError(null);

    setTracks((currentTracks) =>
      currentTracks.map((track) =>
        track.id === trackId
          ? { ...track, likes: track.likes + 1, isLiked: true }
          : track
      )
    );
    localStorage.setItem(`liked-${trackId}`, "true");

    let responseBodyText = "";
    try {
      const response = await fetch("/api/likes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ songId: trackId }),
      });

      responseBodyText = await response.text(); // Read body once as text

      if (!response.ok) {
        let errorDetail = response.statusText;
        if (responseBodyText && !responseBodyText.trim().startsWith("<")) {
          errorDetail = responseBodyText;
        } else if (responseBodyText) {
          console.error(
            `HTML error received on like track (status ${response.status}):`,
            responseBodyText.substring(0, 500)
          );
        }
        throw new Error(`API Error: ${response.status} - ${errorDetail}`);
      }

      // If response.ok, try to parse the text as JSON
      const updatedTrackFromServer = JSON.parse(responseBodyText);

      setTracks((currentTracks) =>
        currentTracks.map((track) =>
          track.id === trackId
            ? { ...track, likes: updatedTrackFromServer.likes, isLiked: true }
            : track
        )
      );
    } catch (err) {
      // Catches fetch errors, text reading, JSON parsing, or thrown new Error
      console.error("Failed to like track:", err.message);
      if (responseBodyText && err.message.includes("API Error")) {
        // Only log raw if it was part of API error
        // The error message already includes details from responseBodyText if it wasn't HTML
      } else if (responseBodyText) {
        console.error(
          `Raw response for like track (ID: ${trackId}) (that caused error):`,
          responseBodyText.substring(0, 500)
        );
      }
      setError(err.message);
      setTracks((currentTracks) =>
        currentTracks.map((track) =>
          track.id === trackId
            ? { ...track, likes: track.likes - 1, isLiked: false }
            : track
        )
      );
      localStorage.removeItem(`liked-${trackId}`);
    }
    setIsLiking(null);
  };

  if (isLoading)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem" }}>
        Loading music likes...
      </p>
    );
  if (error)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem", color: "red" }}>
        Error: {error}
      </p>
    );

  return (
    <div className="music-page-container">
      <h1>My Music</h1>
      <div className="music-grid">
        {tracks.map((track) => (
          <MusicCard
            key={track.id}
            track={track}
            onLikeTrack={handleLikeTrack}
            isLiking={isLiking} // Pass down the liking state for individual button disabling
          />
        ))}
      </div>
    </div>
  );
};

export default MusicPage;
